import { Flex } from "@chakra-ui/react";
import { v4 as uuidv4 } from "uuid";
import React, { useState, useEffect } from "react";
import {
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Button,
  useDisclosure,
  Link,
  DrawerContent,
  DrawerCloseButton,
} from "@chakra-ui/react";
import Footer from "./chat/Footer";
import Messages from "./chat/Messages";
import NavBar from "./NavBar";
import axios from "axios";

import "../index.css";

import { useAccount } from "../lib/accountContext";
import { useRedirectToLogin } from "../lib/hooks";
import Paywall from "./accounts/Paywall";
import toast, { Toaster } from 'react-hot-toast';

const Chat = (props) => {
  const account = useAccount();

  const [messages, setMessages] = useState([]);
  const [sessionID, setSessionID] = useState(uuidv4());
  const [inputMessage, setInputMessage] = useState("");
  const [sessions, setSessions] = useState([]);
  const [showCancelButton, setShowCancelButton] = useState(false);
  const [shouldScroll, setShouldScroll] = useState(false);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = React.useRef();
  useRedirectToLogin();

  // Sets isSubscribed to true for everyone if ENABLE_PAYWALL is set to false
  const ENABLE_PAYWALL = false;
  const isSubscribed = ENABLE_PAYWALL ? account.isSubscribed : true;

  const abortController = new AbortController();
  const signal = abortController.signal;

  const getMessages = async () => {
    const response = await fetch(axios.defaults.baseURL + "/chat-sessions", {
      headers: {
        "Content-Type": "application/json",
        ...axios.defaults.headers,
      },
    });
    return await response.json();
  };

  useEffect(() => {
    const fetchData = async () => {
      if (isSubscribed) {
        const sessionList = await getMessages();
        setSessions(Object.entries(sessionList));
      }
    };

    fetchData();
    localStorage.setItem('stop', null);
  }, []);

  const { generatorData } = props;

  const handleSendMessage = async () => {
    if (!inputMessage.trim().length || !inputMessage.trim().length === 0) {
      return;
    }

    setShouldScroll(true);

    const data = inputMessage;
    const newMessages = [...messages, { role: "user", content: data }];
    setMessages(newMessages);
    setInputMessage("");

    try {
      const response = await fetch(axios.defaults.baseURL + generatorData.api, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...axios.defaults.headers,
        },
        body: JSON.stringify({ sessionID: sessionID, messages: newMessages }),
        signal: signal,
      }).catch((error) => {
        setShouldScroll(false);
        toast.error("Sorry, there was an issue retrieving the items. We are working on it.");
        return;
      });

      if (!response.ok) {
        setShouldScroll(false);
        toast.error("Sorry, there was an issue retrieving the items. We are working on it.");
        return;
      }
      const chatdb_response = response.body;
      if (!chatdb_response) {
        setShouldScroll(false);
        return;
      }

      const reader = chatdb_response.getReader();
      const decoder = new TextDecoder();

      let done = false;
      setMessages((old) => [...old, { role: "assistant", content: "" }]);
      setShowCancelButton(true);

      while (!done) {
        if (localStorage.getItem('stop') !== 'stop') {
          const { value, done: doneReading } = await reader.read();
          done = doneReading;
          const chunkValue = decoder.decode(value);
          if (chunkValue !== undefined) {
            setMessages((oldMessages) => {
              const lastMessage = oldMessages[oldMessages.length - 1];
              const updatedLastMessage = {
                ...lastMessage,
                content: lastMessage.content + chunkValue,
              };
              return [...oldMessages.slice(0, -1), updatedLastMessage];
            });
          }
        } else {
          done = true;
          abortController.abort(); // Cancel the request
        }
      }
      localStorage.setItem('stop', null);
      setShowCancelButton(false);
      setShouldScroll(false);
    } catch (error) {
      // Handle network error
      setShouldScroll(false);
      toast.error("Sorry, there was an issue retrieving the items. We are working on it.");
      return;
    }
  };
  // get chats from Redis
  const openChats = async () => {
    onOpen();
    const sessionList = await getMessages();
    setSessions(Object.entries(sessionList || []).reverse());
  };

  // Change the chat to a different one
  const selectChat = (id) => {
    const new_messages = sessions.find((session) => session[0] === id);
    setMessages(new_messages[1]);
    setSessionID(new_messages[0]);
    onClose();
  };

  const deleteChat = async (id) => {
    const existingSessions = sessions.filter((session) => session[0] !== id);
    if (existingSessions.length > 0) {
      if (sessionID === id) {
        setMessages(existingSessions[0][1]);
        setSessionID(existingSessions[0][0]);
      }
    } else {
      setMessages([]);
      setSessionID(uuidv4());
    }
    setSessions(existingSessions || []);

    try {
      const response = await fetch(axios.defaults.baseURL + "/chat-sessions", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          ...axios.defaults.headers,
        },
        body: JSON.stringify({ sessionID: id }),
      });
    } catch (error) {
      console.error(error);
    }
  };

  const createNewChat = () => {
    setMessages([]);
    setSessionID(uuidv4());
    onClose();
  };

  const clickCancelButton = () => {
    setShowCancelButton(false);
    localStorage.setItem('stop', 'stop');
  }

  return isSubscribed === false ? (
    <Paywall />
  ) : (
    <>
      <NavBar />
      <Button marginTop="1rem" marginX="1rem" ref={btnRef} onClick={openChats}>
        Conversations
      </Button>
      <Flex w="100%" h="85vh" flexDir="column">
        <Messages messages={messages} shouldScroll={shouldScroll} />
        {showCancelButton ?
          (<div className="text-center">
            <button className="rounded-full bg-red-500 w-32 h-8 mt-5 text-white" onClick={() => clickCancelButton()}>
              <span className="inline-block w-3 h-3 bg-white rounded-sm mr-2"></span>
              Stop
            </button>
          </div>) : ''
        }
        <Footer
          shouldScroll={shouldScroll}
          inputMessage={inputMessage}
          setInputMessage={setInputMessage}
          handleSendMessage={handleSendMessage}
        />
      </Flex>
      <Drawer
        isOpen={isOpen}
        placement="left"
        onClose={onClose}
        finalFocusRef={btnRef}
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton className="text-white" />
          <DrawerHeader className="bg-[#303a52] text-white">
            Your Conversations
          </DrawerHeader>
          <DrawerBody className="bg-[#303a52]">
            {sessions.map((session, index) => (
              <div
                key={index}
                className="card hover:cursor-pointer hover:border-solid bg-slate-50 hover:scale-105 transition ease-in-out delay-50 w-full my-3 shadow-xl"
                onClick={() => selectChat(session[0])}
              >
                <div className="card-body p-[1rem]">
                  <p className="text-black">
                    {session[1][session[1].length - 2].content?.length > 100
                      ? session[1][session[1].length - 2].content.substring(
                        0,
                        "100"
                      ) + "..."
                      : session[1][session[1].length - 2].content}
                  </p>
                  <div className="card-actions justify-end">
                    <button
                      className="btn z-10 btn-outline btn-circle btn-sm"
                      onClick={(event) => {
                        event.stopPropagation();
                        deleteChat(session[0]);
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-3 w-3"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </DrawerBody>
          <DrawerFooter>
            <button className="btn btn-outline w-full" onClick={createNewChat}>
              New Chat
            </button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
      <Toaster
        position="top-center"
        reverseOrder={false}
      />
    </>
  );
};

export default Chat;
